import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState } from "../components/common";
import { Input } from "../components/ui/input";
import { FileText, Search } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function TemplatesPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [jur, setJur] = useState("all");
  const [categories, setCategories] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [params] = useSearchParams();
  const caseId = params.get("case");

  useEffect(() => {
    api.get("/templates").then((r) => setItems(r.data));
    api.get("/templates-categories").then((r) => {
      setCategories(r.data.categories || []);
      setJurisdictions(r.data.jurisdictions || []);
    });
  }, []);

  const filtered = useMemo(
    () =>
      items.filter((t) => {
        const matchQ = !q || t.title.toLowerCase().includes(q.toLowerCase()) || t.description.toLowerCase().includes(q.toLowerCase());
        const matchC = cat === "all" || t.category === cat;
        const matchJ = jur === "all" || t.jurisdiction === jur;
        return matchQ && matchC && matchJ;
      }),
    [items, q, cat, jur],
  );

  const jurToneStyle = (j) => {
    const palette = {
      Generic: "text-[#4A5568]",
      "United States": "text-[#8a2121]",
      India: "text-[#8a6b1f]",
      "European Union": "text-[#3D1F85]",
    };
    return palette[j] || "text-[#4A5568]";
  };

  return (
    <div data-testid="templates-page">
      <PageHeader
        eyebrow="La Bibliothèque"
        title="Templates"
        description="Pre-built legal documents — generic, US, India, and EU/GDPR jurisdiction packs."
      />
      <div className="flex flex-wrap gap-3 mb-8 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search templates…"
            className="rounded-none border-[#E8E3F0] bg-white pl-9 focus-visible:ring-0 focus-visible:border-[#2A0F5A]"
            data-testid="templates-search-input"
          />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="rounded-none border-[#E8E3F0] bg-white w-[180px]" data-testid="templates-category-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={jur} onValueChange={setJur}>
          <SelectTrigger className="rounded-none border-[#E8E3F0] bg-white w-[200px]" data-testid="templates-jurisdiction-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All jurisdictions</SelectItem>
            {jurisdictions.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={FileText} title="No matching templates" />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E8E3F0] border border-[#E8E3F0]">
          {filtered.map((t) => (
            <Link
              key={t.id}
              to={`/app/templates/${t.id}${caseId ? `?case=${caseId}` : ""}`}
              className="bg-white p-6 hover-lift flex flex-col"
              data-testid={`template-card-${t.id}`}
            >
              <FileText size={18} strokeWidth={1.5} className="text-[#C5A059] mb-4" />
              <div className="flex items-center gap-2 mb-1">
                <span className="label-eyebrow">{t.category}</span>
                <span className={`label-eyebrow ${jurToneStyle(t.jurisdiction)}`}>· {t.jurisdiction}</span>
              </div>
              <div className="font-serif text-2xl leading-tight mb-2">{t.title}</div>
              <div className="text-sm text-[#2A0F5A]/70 leading-relaxed">{t.description}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
